import { AppLayout } from '@/components/layout/AppLayout'
import { AtlasPage } from '@/components/ui/AtlasPage'
import { PageTitle } from '@/components/ui/PageTitle'
import { Separator } from '@/components/ui/Separator'
import { PageNumber } from '@/components/ui/PageNumber'
import { Button } from '@/components/ui/Button'
import { Link } from '@inertiajs/react'
import type { WeatherDashboard } from '@/types/weather'
import { WeatherIcon } from '@/lib/icons'

interface Stats {
  total: number
  healthy: number
  needs_attention: number
  sick: number
  overdue_tasks: number
  last_care: { performed_at: string } | null
}

interface PlantSummary {
  id: number
  name: string
  species_name: string | null
  days_until_watering: number | null
}

interface DueTask {
  id: number
  plant_id: number
  task_type_pl: string
  next_due_at: string | null
  overdue: boolean
  plant_name: string
}

interface Props {
  stats: Stats
  plants_needing_water: PlantSummary[]
  due_tasks: DueTask[]
  weather: WeatherDashboard | null
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}

function todayFormatted(): string {
  return new Date().toLocaleDateString('pl-PL', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  })
}


const ADVISORY_LABELS: Record<string, { text: string; color: string }> = {
  reduce: { text: 'Ogranicz podlewanie', color: 'text-botanical' },
  normal: { text: 'Podlewanie normalne', color: 'text-ink' },
  increase: { text: 'Zwiększ podlewanie', color: 'text-gold' },
}

function DashboardIndex({ stats, plants_needing_water, due_tasks, weather }: Props) {
  const statCards = [
    { label: 'Rośliny ogółem', value: stats.total, color: 'text-ink' },
    { label: 'Zdrowe', value: stats.healthy, color: 'text-botanical' },
    { label: 'Wymaga uwagi', value: stats.needs_attention, color: 'text-gold' },
    { label: 'Zaległe zadania', value: stats.overdue_tasks, color: stats.overdue_tasks > 0 ? 'text-danger' : 'text-ink' },
  ]

  return (
    <AtlasPage>
      <PageTitle title="Przegląd" subtitle={todayFormatted()} />
      <Separator />

      {/* Census grid */}
      <div className="grid grid-cols-2 gap-md md:grid-cols-4">
        {statCards.map((card) => (
          <div key={card.label} className="text-center">
            <p className={`font-display text-3xl ${card.color}`}>{card.value}</p>
            <p className="font-label text-xs text-frame uppercase tracking-wide mt-xs">
              {card.label}
            </p>
          </div>
        ))}
      </div>

      {/* Weather — today only */}
      {weather && (
        <>
          <Separator />
          <Link href="/weather" className="block hover:opacity-80 transition-opacity">
            <div className="grid grid-cols-2 gap-md md:grid-cols-4">
              <div className="text-center flex flex-col items-center">
                <div className="h-[2.25rem] flex items-center">
                  <WeatherIcon code={weather.current.weather_code} size={32} />
                </div>
                <p className="font-label text-xs text-frame uppercase tracking-wide mt-xs">
                  {weather.current.weather_description}
                </p>
              </div>
              <div className="text-center">
                <p className="font-display text-2xl">{Math.round(weather.current.temperature)}°C</p>
                <p className="font-label text-xs text-frame uppercase tracking-wide mt-xs">
                  Temperatura
                </p>
              </div>
              <div className="text-center">
                <p className="font-display text-2xl">{weather.current.humidity}%</p>
                <p className="font-label text-xs text-frame uppercase tracking-wide mt-xs">
                  Wilgotność
                </p>
              </div>
              <div className="text-center">
                <p className="font-display text-2xl">
                  {weather.plant_impact.uv_peak_today != null
                    ? weather.plant_impact.uv_peak_today.toFixed(1)
                    : '\u2014'}
                </p>
                <p className="font-label text-xs text-frame uppercase tracking-wide mt-xs">
                  UV maks.
                </p>
              </div>
            </div>
          </Link>

          {/* Plant impact */}
          {(weather.plant_impact.watering_advisory !== 'normal' || weather.plant_impact.frost_warning || weather.plant_impact.heat_warning) && (
            <div className="mt-md border border-frame/30 bg-paper-dark px-md py-sm text-center">
              {weather.plant_impact.watering_advisory !== 'normal' && (() => {
                const advisory = ADVISORY_LABELS[weather.plant_impact.watering_advisory]
                return (
                  <p className={`font-label text-sm ${advisory.color}`}>
                    {advisory.text}
                    {weather.plant_impact.advisory_reason && (
                      <span className="text-frame italic"> — {weather.plant_impact.advisory_reason}</span>
                    )}
                  </p>
                )
              })()}
              {weather.plant_impact.frost_warning && (
                <p className="font-label text-xs text-danger mt-xs">
                  Uwaga: prognozowane temperatury poniżej 5°C
                </p>
              )}
              {weather.plant_impact.heat_warning && (
                <p className="font-label text-xs text-danger mt-xs">
                  Uwaga: prognozowane temperatury powyżej 35°C
                </p>
              )}
            </div>
          )}
        </>
      )}

      {/* Pilne podlewanie */}
      {plants_needing_water.length > 0 && (
        <>
          <Separator />
          <h2 className="font-display text-lg text-danger text-center uppercase tracking-wider">
            Pilne podlewanie
          </h2>
          <ul className="mt-sm space-y-xs">
            {plants_needing_water.map((plant) => (
              <li key={plant.id} className="text-center">
                <Link
                  href={`/plants/${plant.id}`}
                  className="font-label text-botanical underline decoration-botanical/30 hover:decoration-botanical"
                >
                  {plant.name}
                </Link>
                {plant.species_name && (
                  <span className="text-frame text-xs italic ml-xs">({plant.species_name})</span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Zaległe zadania */}
      {due_tasks.length > 0 && (
        <>
          <Separator />
          <h2 className="font-display text-lg text-gold text-center uppercase tracking-wider">
            Zaległe zadania
          </h2>
          <ul className="mt-sm space-y-xs">
            {due_tasks.map((task) => (
              <li key={task.id} className="flex items-center justify-center gap-sm text-sm">
                <span className={`font-label ${task.overdue ? 'text-danger font-semibold' : 'text-ink'}`}>
                  {task.task_type_pl}
                </span>
                <span className="text-frame">—</span>
                <Link
                  href={`/plants/${task.plant_id}`}
                  className="font-label text-botanical underline decoration-botanical/30"
                >
                  {task.plant_name}
                </Link>
                {task.next_due_at && (
                  <span className="text-frame text-xs">
                    ({formatDate(task.next_due_at)})
                  </span>
                )}
              </li>
            ))}
          </ul>
        </>
      )}

      {/* Last care */}
      {stats.last_care && (
        <>
          <Separator />
          <p className="text-center text-xs text-frame italic">
            Ostatnia pielęgnacja: {formatDate(stats.last_care.performed_at)}
          </p>
        </>
      )}

      {/* Buttons */}
      <Separator />
      <div className="flex items-center justify-center gap-md">
        <Button href="/plants">Rośliny</Button>
        <Button href="/plants/new" variant="secondary">Dodaj roślinę</Button>
      </div>

      <PageNumber number={1} />
    </AtlasPage>
  )
}

DashboardIndex.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
export default DashboardIndex
